/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import {
    FetchMethod, FetchOptions, IndexedObject, PDFElementParams, PdfFont, ResponseData, sendFetch as controlFetch
} from '@dvrd/dvr-controls';
import {createParamUrl, URLS} from './api';
import {onTrialEnd} from './events';
import {
    AutoUploadType, BolReviewSorting, BolReviewTableSorting, CancelType, Country, CustomerType, DataSplitMethod,
    EmailFlow, ExportNameAttr, FilenameBase, Fulfilment, InvoiceExportType, InvoiceFilter, InvoiceUploadRule,
    InvoiceUploadStatus, NullString, OrderDirection, PaymentFailureStatus, ProcessType, ProductReviewAnalyse,
    RequestOptions,
    ReturnProcessingResult, ReviewSalesPeriod, SalesDashboardOrder, SalesDashboardPeriod, SendCostType, StripeCustomer,
    SubscriptionStatus, SystemMessageType, UnlayerDesign, UserFilter, UserRight, UserRole, UserStatus, UserType,
    VATValue
} from './interfaces';
import {Rating} from './types';
import {requireMaintenanceLogin} from './maintenanceUtil';
import {SalesFilter} from './salesDashboardUtil';
import {getCSRF, setCSRF} from './storageUtil';
import {NotificationInterval, NotificationType, SubscriptionModuleName} from './enums';

interface ID {
    id: string,
}

interface Callback {
    callback: (data: ResponseData, response: Response) => void,
}

interface WithAbortController {
    abortController?: AbortController;
}

type AbortCallback = {onAbort: VoidFunction};

interface ErrorCallback {
    errorCallback?: (response: Response) => void;
}

interface Data {
    data: object,
}

interface WithData<T> {
    data: T;
}

interface WithPartialData<T> {
    data?: Partial<T>;
}

interface OptionalID {
    id?: string | null,
}

interface WithID {
    id: string;
}

interface WithPagination {
    limit: number;
    page: number;
}

interface WithAccountID {
    account_id: string;
}

interface WithEAN extends WithAccountID {
    ean: string;
}

/*
 ================================================
 SUBSCRIPTION PROTECTED REQUESTS
 ================================================
 */
const subscriptionRequiredFetch = (options: FetchOptions) => {
    const origCallback = options.callback;
    options.callback = (data: ResponseData, response: Response) => {
        if (!data.success && data.message === 'subscription-failure' && data.type !== 'auto_send') {
            onTrialEnd();
            data.handled = true;
        }
        if (origCallback) origCallback(data, response);
    };
    return bolmateFetch(options);
};

/*
 ================================================
 AUTH
 ================================================
 */
interface RegisterData {
    email: string;
    password: string;
    firstname: string;
    lastname?: string;
    flex?: object;
    invitation_id: string | null;
}

interface LoginData {
    email: string;
    password: string;
}

interface GoogleAuth {
    token: string;
}

export const register = (params: Callback & WithData<RegisterData>) => {
    bolmateFetch({
        url: URLS.POST.REGISTER,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const login = (params: Callback & WithData<LoginData>) => {
    bolmateFetch({
        url: URLS.POST.LOGIN,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const logout = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.LOGOUT,
        callback: (responseData: ResponseData, response: Response) => {
            params.callback(responseData, response);
        }
    });
};

export function googleLogin(params: WithData<GoogleAuth> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GOOGLE_LOGIN,
        method: FetchMethod.POST,
        ...params
    });
}

export function googleRegister(params: WithData<GoogleAuth> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GOOGLE_REGISTER,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 USER
 ================================================
 */
interface UpdatePassword {
    password: string | null;
    new_password: string;
    activation_key: string | null;
}

interface ResetPassword {
    email: string;
}

interface SetVatNumber {
    vat_number: string;
}

type WithEmail = {email: string;};

type CheckEmailExists = WithEmail & WithAccountID & {
    check_account_access?: boolean;
}

interface ReferAFriend {
    friend_email: string;
}

export const getUser = (params: OptionalID & Callback & ErrorCallback) => {
    bolmateFetch({
        url: URLS.GET.USER,
        callback: params.callback,
        errorCallback: params.errorCallback
    });
};

export const updateUser = (params: Data & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_USER,
        method: FetchMethod.PUT,
        data: params.data,
        callback: params.callback
    });
};

export const resetPassword = (params: WithData<ResetPassword> & Callback) => {
    bolmateFetch({
        url: URLS.POST.RESET_PASSWORD,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const updatePassword = (params: WithData<UpdatePassword> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.CHANGE_PASSWORD,
        method: FetchMethod.PUT,
        data: params.data,
        callback: params.callback
    });
};

export const deleteUser = (params: ID & Callback) => {
    bolmateFetch({
        url: URLS.DELETE.DELETE_USER,
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export function setVatNumber(params: WithData<SetVatNumber> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SET_VAT_NUMBER,
        method: FetchMethod.POST,
        ...params
    });
}

export function unsubscribeMonthlyMail(params: ID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.UNSUBSCRIBE_MONTHLY_MAIL, {user_id: params.id}),
        callback: params.callback
    });
}

export function emailExists(params: WithData<CheckEmailExists> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EMAIL_EXISTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function userHasSubscription(params: Callback) {
    return bolmateFetch({url: URLS.GET.USER_HAS_SUBSCRIPTION, ...params});
}

export function referAFriend(params: WithData<ReferAFriend> & Callback) {
    return bolmateFetch({
        url: URLS.POST.REFER_A_FRIEND,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 2FA
 ================================================
 */
interface WithToken {
    token: string;
    jwt_token: string;
}

export const generate2FaToken = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.GENERATE_TWO_FACTOR,
        callback: params.callback
    });
};

export const validate2FaToken = (params: WithData<WithToken> & Callback) => {
    bolmateFetch({
        url: URLS.POST.VALIDATE_TWO_FACTOR,
        method: FetchMethod.POST,
        ...params
    });
};

export const disable2Fa = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.DISABLE_TWO_FACTOR,
        ...params
    });
};

/*
 ================================================
 PDF Template
 ================================================
 */
interface CreateTemplate {
    account_id: string;
    elements: PDFElementParams<any, any>[];
    main_font: PdfFont;
}

type UpdateTemplate = WithData<Partial<CreateTemplate> & {template_id: string; image?: File}>;

interface PreviewTemplate {
    account_id: string;
    data: CreateTemplate & {image?: File} | IndexedObject<any>;
}

interface CopyFromAccount extends WithAccountID {
    from_account_id: string;
}

export const createPDFTemplate = (params: WithData<CreateTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_TEMPLATE,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const previewTemplate = (params: PreviewTemplate & Callback) => {
    const data = createPdfTemplateData(params);
    bolmateFetch({
        url: createParamUrl(URLS.POST.PREVIEW_TEMPLATE,
            {account_id: params.account_id}),
        data,
        method: FetchMethod.POST,
        callback: params.callback,
        stringifyData: !(data instanceof FormData)
    });
};

export const updatePDFTemplate = (params: UpdateTemplate & Callback) => {
    const data = createPdfTemplateData(params);
    bolmateFetch({
        url: URLS.PUT.UPDATE_TEMPLATE,
        method: FetchMethod.PUT,
        data,
        callback: params.callback,
        stringifyData: !(data instanceof FormData)
    });
};

const createPdfTemplateData = (params: WithData<Partial<CreateTemplate> & {template_id?: string; image?: File}>) => {
    let data: FormData | IndexedObject<any> = params.data;
    if (params.data.image) {
        data = new FormData();
        data.set('image', params.data.image);
        for (const key of Object.keys(params.data)) {
            if (key === 'image') continue;
            // @ts-ignore
            data.set(key, JSON.stringify(params.data[key]));
        }
    }
    return data;
};

export function copyPDFTemplate(params: WithData<CopyFromAccount> & Callback) {
    return bolmateFetch({
        url: URLS.POST.COPY_PDF_TEMPLATE,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 PDFElement
 ================================================
 */

/*
 ================================================
 Account
 ================================================
 */

interface CreateAccount {
    company_name: string;
    address?: string | null;
    zip_code?: string | null;
    city?: string | null;
    country?: string | null;
    coc_number: string | null;
    vat_number: string | null;
    email: string;
    reply_email?: string | null;
    client_id?: string | null;
    client_secret?: string | null;
    settings?: IndexedObject<any>;
    logo?: File | null;
    tel_number?: string | null;
    default_vat?: number;
    invoice_prefix?: string;
    invoice_start?: string;
    use_bol_text?: boolean;
    use_platform_text?: boolean;
    email_template?: string;
    email_subject?: string | null;
    email_bcc?: string | null;
    iban?: string | null;
    sender_name: string | null;
    send_canceled_invoices?: boolean;
    vat_numbers?: IndexedObject<string>;
    flex?: IndexedObject<any>;
    platform_label?: string | null;
    reverse_vat_countries?: string[] | null;
    auto_respond_old_orders: boolean;
    old_order_invoice_prefix: string;
    old_order_invoice_start: string;
}

interface UpdateAccount extends Partial<CreateAccount> {
    account_id: string;
    auto_send_invoices?: boolean;
    email_max_product_name_length?: number | null;
    filename_base?: FilenameBase;
    auto_respond_invoice_requests?: boolean;
    proactively_upload_invoices?: boolean;
    invoice_upload_rules?: Array<InvoiceUploadRule>;
    notify_missing_invoice?: boolean;
    notify_missing_invoice_mail?: string | null;
    include_ubl_attachment?: boolean;
    participate_kor?: boolean;
}

export const createAccount = (params: WithData<CreateAccount> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_ACCOUNT,
        method: FetchMethod.POST,
        data: createAccountData(params),
        callback: params.callback,
        stringifyData: !(params.data.logo instanceof File)
    });
};

export const getAccount = (params: {account_id: string} & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_ACCOUNT, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const updateAccount = (params: WithData<UpdateAccount> & Callback) => {
    return subscriptionRequiredFetch({
        url: URLS.PUT.UPDATE_ACCOUNT,
        method: FetchMethod.PUT,
        data: createAccountData(params),
        callback: params.callback,
        stringifyData: !(params.data.logo instanceof File)
    });
};

const createAccountData = (params: WithData<CreateAccount | UpdateAccount>) => {
    let data;
    if (params.data.logo) {
        data = new FormData();
        for (const key of Object.keys(params.data)) {
            if (key !== 'logo') {
                let value = params.data[key as keyof CreateAccount];
                if (typeof value !== 'string') value = JSON.stringify(value);
                if (value) data.set(key, value as string);
            }
        }
        data.set('logo', params.data.logo);
    } else data = params.data;
    return data;
};

export const pingAccountStatus = (params: ID & Callback) => {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ACCOUNT_STATUS, {account_id: params.id}),
        callback: params.callback
    });
};

export const deleteAccount = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_ACCOUNT, {account_id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

/*
 ================================================
 Attachment
 ================================================
 */

interface UploadAttachment {
    account_id: string;
    file: File;
}

interface GetAttachments {
    limit?: number;
    page?: number;
    search_value?: string;
    account_id: string;
}

interface FindAttachments {
    account_id: string;
    attachment_ids: string[];
}

interface DeleteAttachment {
    attachment_id: string;
    account_id: string;
}

export const uploadAttachment = (params: UploadAttachment & Callback) => {
    const data = new FormData();
    data.set('file', params.file);
    bolmateFetch({
        url: createParamUrl(URLS.POST.UPLOAD_ATTACHMENT, {account_id: params.account_id}),
        method: FetchMethod.POST,
        data,
        stringifyData: false,
        callback: params.callback
    });
};

export const getAttachments = (params: WithData<GetAttachments> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_ATTACHMENTS,
        method: FetchMethod.POST,
        ...params
    });
};

export const findAttachments = (params: WithData<FindAttachments> & Callback) => {
    bolmateFetch({
        url: URLS.POST.FIND_ATTACHMENTS,
        method: FetchMethod.POST,
        ...params
    });
};

export const deleteAttachment = (params: WithData<DeleteAttachment> & Callback) => {
    bolmateFetch({
        url: URLS.POST.DELETE_ATTACHMENT,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 Email Step
 ================================================
 */

interface WithEmailStepId {
    email_step_id: string;
}

interface CreateEmailStep {
    account_id: string;
    label: string;
    flow: EmailFlow;
    days?: number | null;
    template_id?: string | null;
    product_eans?: string[] | null;
    attachment_ids?: string[] | null;
    with_invoice: boolean;
    active: boolean;
    customer_type: CustomerType;
    only_work_days: boolean;
    countries: string[] | null;
    fulfilment: Fulfilment;
}

type UpdateEmailStep = Partial<CreateEmailStep> & WithAccountID & WithEmailStepId;

interface UpdateAllEmailSteps {
    account_id: string;
    email_steps: Array<UpdateEmailStep>;
}

interface EmailStepStats {
    email_step_id: string;
    split_method: DataSplitMethod;
    period_start?: string | null;
    period_end?: string | null;
    account_id: string;
}

interface EmailStepStatsTotal {
    email_step_id: string;
    account_id: string;
}

interface GetAdminEmailSteps {
    accountId: string;
    userId: string;
}

interface ToggleAllServices {
    toggled: boolean;
}

type GetEmailSteps = WithAccountID & {
    search: string | null;
}

type LinkEmailProducts = WithAccountID & {
    file: File;
}

export const emailStepsAreEnabled = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.EMAILS_ENABLED, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const createEmailStep = (params: WithData<WithAccountID & CreateEmailStep> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_EMAIL_STEP,
        method: FetchMethod.POST,
        ...params
    });
};

export const getEmailSteps = (params: WithData<GetEmailSteps> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_EMAIL_STEPS,
        method: FetchMethod.POST,
        ...params
    });
};

export function getAdminEmailSteps(params: GetAdminEmailSteps & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_EMAIL_STEPS, {user_id: params.userId, account_id: params.accountId}),
        callback: params.callback
    });
}

export const updateEmailStep = (params: WithData<UpdateEmailStep> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_EMAIL_STEP,
        method: FetchMethod.PUT,
        ...params
    });
};

export const updateAllEmailSteps = (params: WithData<UpdateAllEmailSteps> & Callback) => {
    bolmateFetch({
        url: URLS.PATCH.PATCH_EMAIL_STEPS,
        method: FetchMethod.PATCH,
        ...params
    });
};

export const deleteEmailStep = (params: WithAccountID & WithEmailStepId & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_EMAIL_STEP, {
            account_id: params.account_id,
            email_step_id: params.email_step_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export const getEmailStepStats = (params: WithData<EmailStepStats> & Callback) => {
    bolmateFetch({
        url: URLS.POST.EMAIL_STEP_STATS,
        method: FetchMethod.POST,
        ...params
    });
};

export const getEmailStepStatsTotal = (params: EmailStepStatsTotal & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_EMAIL_STEP_STATS_TOTAL, {
            account_id: params.account_id,
            email_step_id: params.email_step_id
        }),
        method: FetchMethod.GET,
        callback: params.callback
    });
};

export function getGlobalEmailStats(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GLOBAL_EMAIL_STATS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getGlobalEmailPeriodStats(params: WithData<Omit<EmailStepStats, 'email_step_id'>> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GLOBAL_EMAIL_PERIOD_STATS,
        method: FetchMethod.POST,
        ...params
    });
}

export function sendHistoryMails(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.SEND_HISTORY_EMAIL, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function copyEmailSteps(params: WithData<CopyFromAccount> & Callback) {
    return bolmateFetch({
        url: URLS.POST.COPY_EMAIL_STEPS,
        method: FetchMethod.POST,
        ...params
    });
}

export function toggleAllServices(params: WithData<ToggleAllServices> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.TOGGLE_ALL_SERVICES,
        method: FetchMethod.PUT,
        ...params
    });
}

export function linkEmailProducts(params: LinkEmailProducts & Callback) {
    const data = new FormData();
    data.set('file', params.file);
    data.set('account_id', params.account_id);
    return bolmateFetch({
        url: URLS.POST.LINK_EMAIL_PRODUCTS,
        method: FetchMethod.POST,
        data,
        stringifyData: false,
        callback: params.callback
    });
}

/*
 ================================================
 Email Templates
 ================================================
 */

interface CreateEmailTemplate {
    account_id: string;
    name: string;
    subject?: string;
    body?: string;
    with_test_invoice?: boolean;
    mail_json?: IndexedObject<any> | null;
    html_body?: string;
}

interface GetEmailTemplate {
    account_id: string;
    template_id: string;
}

interface GetEmailTemplates {
    limit?: number;
    page?: number;
    account_id: string;
}

interface UpdateEmailTemplate {
    account_id: string;
    template_id: string;
    name: string;
    sender: string;
    reply_to: string | null;
    bcc: string | null;
    subject: string;
    body: string;
    with_test_invoice?: boolean;
    mail_json?: IndexedObject<any> | null;
    html_body?: string;
}

interface TestEmailTemplate {
    account_id: string;
    email: string;
    reply_to: string | null;
    subject: string;
    body: string;
    sender_name: string | null;
    html_body?: string | null;
    email_step_id?: string | null;
}

export const createEmailTemplate = (params: WithData<CreateEmailTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_EMAIL_TEMPLATE,
        method: FetchMethod.POST,
        ...params
    });
};

export const getEmailTemplate = (params: WithData<GetEmailTemplate> & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_EMAIL_TEMPLATE, {
            account_id: params.data.account_id,
            template_id: params.data.template_id
        }),
        method: FetchMethod.GET,
        callback: params.callback
    });
};

export const getEmailTemplates = (params: WithData<GetEmailTemplates> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_EMAIL_TEMPLATES,
        method: FetchMethod.POST,
        ...params
    });
};

export const updateEmailTemplate = (params: WithData<UpdateEmailTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_EMAIL_TEMPLATE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const testEmailTemplate = (params: WithData<TestEmailTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.POST.TEST_EMAIL_TEMPLATE,
        method: FetchMethod.POST,
        ...params
    });
};

export const deleteEmailTemplate = (params: WithAccountID & WithID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_EMAIL_TEMPLATE, {account_id: params.account_id, template_id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export function duplicateEmailTemplate(params: WithAccountID & WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DUPLICATE_TEMPLATE, {account_id: params.account_id, template_id: params.id}),
        callback: params.callback
    });
}

/*
 ================================================
 Invoice
 ================================================
 */

interface WithInvoiceId {
    invoice_id: string;
}

interface GetInvoices {
    limit?: number;
    page?: number;
    filters: InvoiceFilter;
    account_id: string;
}

interface GetInvoiceStats {
    account_id: string;
    period_start?: string;
    period_end?: string;
}

interface DownloadOrSendInvoice extends WithInvoiceId {
    account_id: string;
    noHistory?: true;
}

interface DownloadMultipleInvoices extends WithAccountID {
    invoice_ids: string[] | 'all';
}

interface SendMultipleInvoices extends DownloadMultipleInvoices {
    send_now?: boolean;
}

interface SendInvoiceConfigured extends WithInvoiceId {
    account_id: string;
    mail_text: string;
    mail_json: IndexedObject<any>;
    subject: string;
}

export type UpdateInvoice = DownloadOrSendInvoice & Partial<CreateInvoice>;

export interface CreateInvoice {
    account_id: string;
    date: string;
    reference: string | null;
    company: string;
    firstname: string;
    lastname: string;
    street: string;
    house_number: string;
    house_number_suffix: string | null;
    zip_code: string;
    city: string;
    email: string;
    vat_number: string | null;
    coc_number: string | null;
    items: {
        item_id: string;
        ean: string;
        title: string;
        quantity: number;
        price: number;
        reference: string;
        vat: VATValue;
    }[];
    country?: Country;
    invoice_num?: string;
}

interface ExportInvoiceCount {
    from_date: string;
    to_date: string;
    account_id: string;
    country?: string | null;
    reverse_charge_only?: boolean;
    customer_type?: CustomerType;
    fulfilment: Fulfilment | null;
}

interface ExportInvoices extends ExportInvoiceCount {
    export_type: InvoiceExportType;
    name_attr?: ExportNameAttr;
    invoice_field_names?: Array<string>;
    order_field_names?: Array<string>;
}

interface BulkSequence {
    account_id: string;
    invoice_ids: string[];
}

interface AssignInvoiceNumber extends WithInvoiceId {
    account_id: string;
    invoice_num: string;
    prefix: string | null;
}

export const getMinimalInvoices = (params: WithData<GetInvoices> & Callback & Partial<AbortCallback>) => {
    return bolmateFetch({
        url: URLS.POST.GET_MINIMAL_INVOICES,
        method: FetchMethod.POST,
        ...params
    });
};

export const getInvoice = (params: WithInvoiceId & WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_INVOICE, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
};

export const getInvoiceStats = (params: WithData<GetInvoiceStats> & Callback & Partial<AbortCallback>) => {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.INVOICE_STATS, {account_id: params.data.account_id}),
        callback: params.callback,
        onAbort: params.onAbort
    });
};

export const downloadInvoice = (params: DownloadOrSendInvoice & Callback) => {
    let url = createParamUrl(URLS.GET.DOWNLOAD_INVOICE, {invoice_id: params.invoice_id, account_id: params.account_id});
    if (params.noHistory) url += '?no-history=1';
    subscriptionRequiredFetch({
        url,
        callback: params.callback
    });
};

export const downloadInvoices = (params: WithData<DownloadMultipleInvoices> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.DOWNLOAD_INVOICES,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const sendInvoice = (params: DownloadOrSendInvoice & Callback) => {
    subscriptionRequiredFetch({
        url: createParamUrl(URLS.GET.SEND_INVOICE, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
};

export function sendInvoiceConfigured(params: WithData<SendInvoiceConfigured> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.SEND_INVOICE_CONFIGURED,
        method: FetchMethod.POST,
        ...params
    });
}

export const sendInvoices = (params: WithData<SendMultipleInvoices> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.SEND_INVOICES,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const updateInvoice = (params: WithData<UpdateInvoice> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.PUT.UPDATE_INVOICE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const getExportInvoiceCount = (params: WithData<ExportInvoiceCount> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_INVOICE_EXPORT_COUNT,
        method: FetchMethod.POST,
        ...params
    });
};

export const exportInvoices = (params: WithData<ExportInvoices> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.EXPORT_INVOICES,
        method: FetchMethod.POST,
        ...params
    });
};

export const getInvoiceHistory = (params: DownloadOrSendInvoice & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.INVOICE_HISTORY, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
};

export const bulkSequence = (params: WithData<BulkSequence> & Callback) => {
    bolmateFetch({
        url: URLS.POST.BULK_SEQUENCE,
        method: FetchMethod.POST,
        ...params
    });
};

export const assignInvoiceNumber = (params: WithData<AssignInvoiceNumber> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.INVOICE_NUMBER,
        method: FetchMethod.PUT,
        ...params
    });
};

export function disableLambda(params: WithInvoiceId & WithAccountID & Callback) {
    bolmateFetch({
        url: createParamUrl(URLS.GET.DISABLE_LAMBDA, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 Credit invoice
 ================================================
 */

export type CreditInvoiceItem = {
    ean: string;
    item_id: string;
    order_item_id: string;
    title: string;
    quantity: number;
    price: number;
    reference: string;
}

interface CreateCreditInvoice {
    account_id: string;
    invoice_id: string;
    items: CreditInvoiceItem[];
    date?: string | null;
    coc_number?: string | null;
    vat_number?: string | null;
}

export type UpdateCreditInvoiceItem = {
    id: string;
    quantity: number;
    price: number;
}

interface UpdateCreditInvoice {
    account_id: string;
    credit_invoice_id: string;
    items: UpdateCreditInvoiceItem[];
    delete_item_ids?: string[];
    add_items?: CreditInvoiceItem[];
    coc_number?: string | null;
    vat_number?: string | null;
}

interface UpdateCreditSettings {
    account_id: string;
    auto_create_credit_invoices?: boolean;
    auto_send_credit_invoices?: boolean;
    credit_invoice_num_start?: string;
    credit_invoice_num_prefix?: string;
}

export const createCreditInvoice = (params: WithData<CreateCreditInvoice> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREDIT_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
};

export const updateCreditInvoice = (params: WithData<UpdateCreditInvoice> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.CREDIT_INVOICE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const updateCreditSettings = (params: WithData<UpdateCreditSettings> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_CREDIT_SETTINGS,
        method: FetchMethod.PUT,
        ...params
    });
};

export const getCreditEmailStep = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.CREDIT_EMAIL_STEP, {account_id: params.account_id}),
        callback: params.callback
    });
};

/*
 ================================================
 Reverse charge invoice
 ================================================
 */

interface CreateReverseCharge {
    account_id: string;
    invoice_id: string;
    vat_number: string;
    country: string;
    skip_validation?: boolean;
}

export const createReverseCharge = (params: WithData<CreateReverseCharge> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_REVERSE_CHARGE,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 Connections
 ================================================
 */

interface TestConnection {
    name: string;
}

export const getConnections = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.CONNECTIONS,
        ...params
    });
};

export const testConnection = (params: TestConnection & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.TEST_CONNECTION, {name: params.name}),
        callback: params.callback
    });
};

/*
 ================================================
 Admin
 ================================================
 */
interface GetAdminUsers {
    limit: number;
    page: number;
    search: NullString;
    filter: UserFilter | null;
}

interface GetChurn {
    from_date: string | null;
    to_date: string | null;
    split_method: DataSplitMethod;
}

interface UpdateAdminUser {
    status: UserStatus;
    type: UserType;
    firstname: string;
    lastname: string;
    flex: object | null;
    settings: object | null;
    tel_number: string | null;
    address: string | null;
    zip_code: string | null;
    city: string | null;
    email: string;
    subscription_status: SubscriptionStatus;
    override_limitations: boolean;
}

interface MixedID {
    user_id: string;
    account_id: string;
}

interface ImportHistory {
    user_id: string;
    account_id: string;
    invoice_num_prefix: string;
    invoice_num_start: string;
    send_emails: boolean;
    import_till_date: string | null;
    start_page?: number;
}

interface EmailSequenceCount {
    user_id: string;
    account_id: string;
    from_date: string | null;
    to_date: string | null;

}

interface AdminEmailSequence extends EmailSequenceCount {
    email_step_ids: Array<string> | null;
}

export function getAdminUsers(params: WithPartialData<GetAdminUsers> & Callback): AbortController | null {
    return bolmateFetch({
        url: URLS.POST.GET_USERS,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
}

export function getChurn(params: WithData<GetChurn> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_CHURN,
        method: FetchMethod.POST,
        ...params
    });
}

export function getAdminAccounts(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_ACCOUNTS, {user_id: params.id}),
        callback: params.callback
    });
}

export function getAdminSubscriptionStats(params: Callback) {
    return bolmateFetch({
        url: URLS.GET.SUBSCRIPTION_STATS,
        ...params
    });
}

export const updateAdminUser = (params: WithPartialData<UpdateAdminUser> & ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.PUT.UPDATE_ADMIN_USER, {user_id: params.id}),
        method: FetchMethod.PUT,
        data: params.data,
        callback: params.callback
    });
};

export const deleteAdminUser = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_ADMIN_USER, {user_id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export const updateAdminAccount = (params: WithData<UpdateAccount> & ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.PUT.UPDATE_ADMIN_ACCOUNT, {user_id: params.id}),
        method: FetchMethod.PUT,
        data: createAccountData(params),
        callback: params.callback,
        stringifyData: !(params.data.logo instanceof File)
    });
};

export const deleteAdminAccount = (params: MixedID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_ADMIN_ACCOUNT, {user_id: params.user_id, account_id: params.account_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export const adminLogin = (params: ID & Callback) => {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_LOGIN, {user_id: params.id}),
        callback: params.callback
    });
};

export function importHistory(params: WithData<ImportHistory> & Callback) {
    return bolmateFetch({
        url: URLS.POST.IMPORT_HISTORY,
        method: FetchMethod.POST,
        ...params
    });
}

export function adminEmailSequence(params: WithData<AdminEmailSequence> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_EMAIL_SEQUENCE,
        method: FetchMethod.POST,
        ...params
    });
}

export function getEmailSequenceCount(params: WithData<EmailSequenceCount> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_EMAIL_SEQUENCE_COUNT,
        method: FetchMethod.POST,
        ...params
    });
}

export function getAdminEmailInfo(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_EMAIL_INFO, {id: params.id}),
        callback: params.callback
    });
}

/*
 ================================================
 Threads
 ================================================
 */
interface GetThreads {
    limit: number,
    page: number
}

export const getThreads = (params: GetThreads & Callback) => {
    let url: string = `${URLS.GET.THREADS}?limit=${params.limit}&page=${params.page}`;
    bolmateFetch({
        url, callback: params.callback
    });
};

export const stopThread = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.STOP_THREAD, {thread_id: params.id}),
        callback: params.callback
    });
};

export const finishThread = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.FINISH_THREAD, {thread_id: params.id}),
        callback: params.callback
    });
};

/*
 ================================================
 System service
 ================================================
 */
interface ToggleService {
    service_name: string;
    enabled: boolean;
}

export const getSystemServices = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.SYSTEM_SERVICES,
        callback: params.callback
    });
};

export const toggleSystemService = (params: WithData<ToggleService> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.TOGGLE_SERVICE,
        method: FetchMethod.PUT,
        ...params
    });
};

/*
 ================================================
 Bol Offers
 ================================================
 */

interface ProductsImport extends WithAccountID {
    use_crawler?: boolean;
}

interface GetProducts extends WithAccountID {
    limit?: number;
    page?: number;
    search?: string;
    eans?: string[];
    sort_custom_vat?: boolean;
    with_images?: boolean;
}

interface GetProductImages extends WithAccountID {
    eans: Array<string>;
}

export function getProducts(params: WithData<GetProducts> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_BOL_PRODUCTS, {account_id: params.data.account_id}),
        data: params.data,
        method: FetchMethod.POST,
        callback: params.callback
    });
}

export function getProductImages(params: WithData<GetProductImages> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_PRODUCT_IMAGES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getProductsCount(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.PRODUCTS_COUNT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export const importProductsAsync = (params: WithData<ProductsImport> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.IMPORT_PRODUCTS_ASYNC,
        method: FetchMethod.POST,
        ...params
    });
};

export const getNumImporting = (params: ProductsImport & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.IMPORTING_PRODUCTS, {account_id: params.account_id}),
        callback: params.callback
    });
};

/*
 ================================================
 API Key
 ================================================
 */

interface ChangeApiKeyStatus {
    account_id: string;
    api_key_id: string;
}

export const getApiKeys = (params: {account_id: string} & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.API_KEYS, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const activateApiKey = (params: WithData<ChangeApiKeyStatus> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.ACTIVATE_API_KEY,
        method: FetchMethod.PUT,
        ...params
    });
};

export function deactivateApiKey(params: ChangeApiKeyStatus & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DEACTIVATE_API_KEY, {
            account_id: params.account_id,
            api_key_id: params.api_key_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Subscription
 ================================================
 */

interface StripePortal extends WithAccountID {
    return_url?: string;
}

interface CreateCheckout extends WithAccountID {
    success_url: string;
    cancel_url: string;
    payment_method?: string;
}

interface SetCancelReason extends WithAccountID {
    reason: string | null;
    cancel_type: CancelType;
}

interface ApplyCoupon extends WithAccountID {
    coupon: string;
}

interface CreateSubscriptionExistingUser {
    address: string;
    zip_code: string;
    city: string;
    country: string;
}

interface WithTaxID extends WithAccountID {
    tax_id: string;
}

export function setupIntent(params: WithAccountID & Callback) {
    bolmateFetch({
        url: createParamUrl(URLS.GET.SETUP_INTENT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export const getStripePortal = (params: WithData<StripePortal> & Callback) => {
    bolmateFetch({
        url: URLS.POST.STRIPE_PORTAL,
        method: FetchMethod.POST,
        ...params
    });
};

export const createCheckout = (params: WithData<CreateCheckout> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CHECKOUT,
        method: FetchMethod.POST,
        ...params
    });
};

export const getSubscriptionUsage = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.SUBSCRIPTION_USAGE, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const getSubscription = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.SUBSCRIPTION, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const setCancelReason = (params: WithData<SetCancelReason> & Callback) => {
    bolmateFetch({
        url: URLS.POST.SET_CANCEL_REASON,
        method: FetchMethod.POST,
        ...params
    });
};

export function applyCoupon(params: WithData<ApplyCoupon> & Callback) {
    return bolmateFetch({
        url: URLS.POST.APPLY_COUPON,
        method: FetchMethod.POST,
        ...params
    });
}

export function discountOnCancel(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DISCOUNT_ON_CANCEL, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function createSubscriptionExistingUser(params: WithData<CreateSubscriptionExistingUser> & Callback) {
    return bolmateFetch({
        url: URLS.POST.CREATE_SUBSCRIPTION_EXISTING_USER,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSubscriptionCustomer(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_SUBSCRIPTION_CUSTOMER, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function updateSubscriptionCustomer(params: WithData<WithAccountID & StripeCustomer> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_CUSTOMER,
        method: FetchMethod.PUT,
        ...params
    });
}

export function getSubscriptionTaxIDs(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_SUBSCRIPTION_TAX_IDS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function addTaxID(params: WithData<WithTaxID> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADD_TAX_ID,
        method: FetchMethod.POST,
        ...params
    });
}

export function deleteTaxID(params: WithTaxID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_TAX_ID, {account_id: params.account_id, tax_id: params.tax_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Subscription Modules
 ================================================
 */
interface WithSubscriptionModuleName extends WithAccountID {
    name: SubscriptionModuleName;
}

interface AddSubscriptionModule extends WithSubscriptionModuleName {
    check_exists?: boolean;
}

export function getSubscriptionModules(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_SUBSCRIPTION_MODULES, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function hasActiveSubscriptionModule(params: WithSubscriptionModuleName & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.HAS_ACTIVE_SUBSCRIPTION_MODULE,
            {account_id: params.account_id, name: params.name}),
        callback: params.callback
    })
}

export function addSubscriptionModule(params: WithData<AddSubscriptionModule> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADD_SUBSCRIPTION_MODULE,
        method: FetchMethod.POST,
        ...params,
    });
}

export function cancelSubscriptionModule(params: WithSubscriptionModuleName & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.CANCEL_SUBSCRIPTION_MODULE, {account_id: params.account_id, name: params.name}),
        method: FetchMethod.DELETE,
        callback: params.callback,
    });
}

/*
 ================================================
 Export process
 ================================================
 */
interface GetExportProcesses {
    account_id: string;
    export_types?: Array<InvoiceExportType>;
    period_start?: string;
    period_end?: string;
}

interface DownloadExportProcess {
    account_id: string;
    export_process_id: string;
}

export const getExportProcesses = (params: WithData<GetExportProcesses> & Callback) => {
    bolmateFetch({
        url: URLS.POST.EXPORT_PROCESSES,
        method: FetchMethod.POST,
        ...params
    });
};

export const downloadExportProcess = (params: WithData<DownloadExportProcess> & Callback) => {
    bolmateFetch({
        url: URLS.POST.EXPORT_PROCESS_DOWNLOAD,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 VAT
 ================================================
 */

interface UpdateDefaultVATs extends WithAccountID {
    default_vats: Record<string, VATValue>;
    apply_retroactive: boolean;
}

interface UpdateBolProduct extends WithAccountID {
    product_id: string;
    vats: Record<string, VATValue>;
    title: string;
    apply_retroactive: boolean;
}

interface ImportVats extends WithAccountID {
    file: File;
    create_non_existing?: boolean;
    apply_retroactive?: boolean;
}

export const getDefaultVats = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.DEFAULT_VATS, {account_id: params.account_id}),
        callback: params.callback
    });
};

export function updateDefaultVATs(params: WithData<UpdateDefaultVATs> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_DEFAULT_VATS,
        method: FetchMethod.PUT,
        ...params
    });
}

export function updateBolProduct(params: WithData<UpdateBolProduct> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_BOL_PRODUCT,
        method: FetchMethod.PUT,
        ...params
    });
}

export function importVats(params: ImportVats & Callback) {
    const formData = new FormData();
    formData.set('file', params.file);
    formData.set('account_id', params.account_id);
    if (params.apply_retroactive) formData.set('apply_retroactive', 'true');
    if (params.create_non_existing) formData.set('create_non_existing', 'true');
    return bolmateFetch({
        url: URLS.POST.IMPORT_VATS,
        method: FetchMethod.POST,
        data: formData,
        stringifyData: false,
        callback: params.callback
    });
}

/*
 ================================================
 SUBSCRIPTION STATS
 ================================================
 */

interface GetSubscriptionStats {
    page: number;
    limit: number;
    split_method: DataSplitMethod;
    from_date?: string;
    to_date?: string;
}

export const getSubscriptionStats = (params: WithData<GetSubscriptionStats> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_SUBSCRIPTION_STATS,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 SYSTEM MESSAGES
 ================================================
 */

interface CreateSystemMessage {
    message: string;
    message_type: SystemMessageType;
    priority: number;
    active?: boolean;
}

interface SystemMessageId {
    message_id: string;
}

export const createSystemMessage = (params: WithData<CreateSystemMessage> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_SYSTEM_MESSAGE,
        method: FetchMethod.POST,
        ...params
    });
};

export const getSystemMessages = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.GET_SYSTEM_MESSAGES,
        ...params
    });
};

export const updateSystemMessage = (params: WithData<Partial<CreateSystemMessage> & SystemMessageId> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_SYSTEM_MESSAGE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const deleteSystemMessage = (params: SystemMessageId & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_SYSTEM_MESSAGE, {message_id: params.message_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

/*
 ================================================
 BOL RETURNS
 ================================================
 */

interface GetBolReturns extends WithAccountID {
    page?: number;
    limit?: number;
    search?: string | null;
    status?: ReturnProcessingResult | null;
}

interface CreditReturn extends WithAccountID {
    return_id: string;
    rma_id: string;
    credit_all_items?: boolean;
}

interface GetReturnsCounts extends WithAccountID {
    period_start: string | null;
    period_end: string | null;
}

export const getBolReturns = (params: WithData<GetBolReturns> & Callback) => {
    return bolmateFetch({
        url: URLS.POST.GET_BOL_RETURNS,
        method: FetchMethod.POST,
        ...params
    });
};

export const getReturnCounts = (params: WithData<GetReturnsCounts> & Callback) => {
    return bolmateFetch({
        url: URLS.POST.RETURN_COUNTS,
        method: FetchMethod.POST,
        ...params
    });
};

export const creditReturnItem = (params: WithData<CreditReturn> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREDIT_RETURNS,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 VAT DECLARATION
 ================================================
 */
interface WithPeriod {
    period_start: string;
    period_end: string;
}

interface GetVatDeclaration extends WithPeriod, WithAccountID {
    split_key: DataSplitMethod;
}

interface GetVatDeclarationWithOptions extends GetVatDeclaration {
    use_api?: boolean;
    recalculate?: boolean;
}

interface DownloadBolInvoice extends WithAccountID {
    file_type: 'pdf' | 'excel';
    invoice_id: string;
}

interface ExportVatDeclaration extends WithPeriod {
    account_id: string;
    export_type: InvoiceExportType;
}

export function getVatDeclaration(params: WithData<GetVatDeclarationWithOptions> & Callback) {
    return bolmateFetch({
        url: URLS.POST.VAT_DECLARATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function downloadBolInvoice(params: DownloadBolInvoice & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DOWNLOAD_BOL_INVOICE, {
            account_id: params.account_id,
            file_type: params.file_type,
            invoice_id: params.invoice_id
        }),
        callback: params.callback
    });
}

export function exportVatDeclaration(params: WithData<ExportVatDeclaration> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_VAT_DECLARATION,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 PROCESS STATUS
 ================================================
 */
interface ProcessStatusByType {
    process_type: ProcessType;
}

interface WithProcessStatusId extends WithAccountID {
    process_id: string;
}

interface AddMissingProducts extends WithProcessStatusId {
    deleteAfter?: boolean;
}

interface UpdateProcessStatus extends WithProcessStatusId {
    flex: IndexedObject<any>;
}

export function getProcessStatus(params: WithProcessStatusId & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.PROCESS_STATUS, {account_id: params.account_id, process_id: params.process_id}),
        callback: params.callback
    });
}

export function getProcessStatusByType(params: ProcessStatusByType & WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.PROCESS_STATUS_BY_TYPE, {
            account_id: params.account_id,
            process_type: params.process_type
        }),
        callback: params.callback
    });
}

export function getAllProcessStatusByType(params: ProcessStatusByType & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ALL_PROCESS_STATUS_BY_TYPE, {process_type: params.process_type}),
        callback: params.callback
    });
}

export function addMissingBolProducts(params: AddMissingProducts & Callback) {
    let url = createParamUrl(URLS.GET.ADD_MISSING_PRODUCTS, {
        account_id: params.account_id,
        process_id: params.process_id
    });
    if (params.deleteAfter) url += '?delete=1';
    return bolmateFetch({
        url,
        callback: params.callback
    });
}

export function deleteProcessStatus(params: WithProcessStatusId & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_PROCESS_STATUS, {
            account_id: params.account_id,
            process_id: params.process_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function updateProcessStatus(params: WithData<UpdateProcessStatus> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_PROCESS_STATUS,
        method: FetchMethod.PUT,
        ...params
    });
}

/*
 ================================================
 BETA
 ================================================
 */

interface SetBetaDecision {
    beta_type: string;
    decision: boolean | null;
}

export function setBetaDecision(params: WithData<SetBetaDecision> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SET_BETA_DECISION,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 BOL OAUTH
 ================================================
 */

interface AuthorizeBolOAuth extends WithAccountID {
    code: string;
}

export function authorizeBolOAuth(params: WithData<AuthorizeBolOAuth> & Callback) {
    return bolmateFetch({
        url: URLS.POST.AUTHORIZE_BOL_OAUTH,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 2FA RESET
 ================================================
 */

interface ResetTwoFactor {
    token: string;
}

interface WithUserID {
    user_id: string;
}

export function resetTwoFactor(params: ResetTwoFactor & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.RESET_TWO_FACTOR, {token: params.token}),
        callback: params.callback
    });
}

export function generateTwoFactorReset(params: WithData<WithUserID> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GENERATE_TWO_FACTOR_RESET,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Invoice number
 ================================================
 */

interface GetLatestInvoiceNumber extends WithAccountID {
    invoice_num_prefix: string;
}

export function getLatestInvoiceNumber(params: WithData<GetLatestInvoiceNumber> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_LATEST_INVOICE_NUM,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Planned process
 ================================================
 */
interface PlanHistoryImport extends WithAccountID {
    invoice_num_prefix: string;
    invoice_num_start: string;
    is_default_num: boolean;
    import_orders: boolean;
    auto_upload: AutoUploadType;
    auto_upload_old: boolean;
    old_invoice_num_prefix: string;
    old_invoice_num_start: string;
}

export function planHistoryImport(params: WithData<PlanHistoryImport> & Callback) {
    return bolmateFetch({
        url: URLS.POST.PLAN_HISTORY_IMPORT,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Invoice request
 ================================================
 */
export type GetInvoiceRequests = WithAccountID & WithPagination & {
    search: string | null;
    status: InvoiceUploadStatus | null;
    country: string | null;
}

type UploadInvoice = WithAccountID & WithInvoiceId & {
    shipment_id?: string | null;
}

interface UploadMultipleInvoices extends WithAccountID {
    upload_all_open: boolean;
    upload_invoice_ids: Array<string> | null;
}

interface ArchiveInvoiceRequest extends WithAccountID {
    order_id: string;
    shipment_id: string;
    customer_account_number: string | null;
    vat_number: string | null;
    kvk_number: string | null;
    request_date: string | null;
}

interface InvoiceRequestSetInvoice extends WithAccountID {
    invoice_request_id: number;
    invoice_id: string;
}

export function getInvoiceRequestStats(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.INVOICE_REQUEST_STATS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getOpenInvoiceRequestCount(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.OPEN_REQUEST_COUNT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getInvoiceRequests(params: WithData<GetInvoiceRequests> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_INVOICE_REQUESTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function uploadInvoice(params: WithData<UploadInvoice> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.UPLOAD_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

export function uploadMultipleInvoices(params: WithData<UploadMultipleInvoices> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.UPLOAD_MULTIPLE_INVOICES,
        method: FetchMethod.POST,
        ...params
    });
}

export function invoiceRequestSetInvoice(params: WithData<InvoiceRequestSetInvoice> & Callback) {
    return bolmateFetch({
        url: URLS.POST.INVOICE_REQUEST_SET_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Product review request
 ================================================
 */

interface GetProductReviews extends WithAccountID {
    ean: string;
    account_id: string;
    sort_type: string;
    limit: string;
}

type GetProductReviewsFilter = WithAccountID & WithPagination & {
    filter_5_star: boolean
    filter_4_star: boolean
    filter_3_star: boolean
    filter_2_star: boolean
    filter_1_star: boolean
    ean: string
    sort_type: BolReviewSorting;
}

interface GetReviewData extends WithAccountID {
    from_date: string | null;
    to_date: string | null;
}

type GetReviewProducts = GetReviewData & WithPagination & {
    search: string | null;
    sorting: BolReviewTableSorting;
    order_direction: OrderDirection;
    period: ReviewSalesPeriod;
}

type GetReviewsFeed = WithAccountID & WithPagination & {
    filter_ratings: Array<number> | null;
};

interface GetReviewProduct extends WithAccountID {
    ean: string;
}

interface AnalyseProductReviews extends WithEAN {
    analyse: ProductReviewAnalyse;
}

type GetSelectableReviewProducts = WithAccountID & WithPagination & {
    search: string | null;
};

export function getProductReviewCounts(params: WithData<GetProductReviews> & Callback & WithAbortController) {
    return subscriptionRequiredFetch({
        url: URLS.POST.PRODUCT_REVIEW_COUNTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewFeed(params: WithData<GetReviewsFeed> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.REVIEW_FEED,
        method: FetchMethod.POST,
        ...params
    });
}

export function getProductReviews(params: WithData<GetProductReviewsFilter> & Callback & WithAbortController) {
    return subscriptionRequiredFetch({
        url: URLS.POST.PRODUCT_REVIEWS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewProducts(params: WithData<GetReviewProducts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_REVIEW_PRODUCTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function archiveInvoiceRequest(params: WithData<ArchiveInvoiceRequest> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.ARCHIVE_INVOICE_REQUEST,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewProduct(params: GetReviewProduct & Callback & WithAbortController) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_REVIEW_PRODUCT, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

export function getReviewTotals(params: WithData<GetReviewData> & Callback) {
    return bolmateFetch({
        url: URLS.GET.GET_REVIEW_TOTALS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewChartData(params: WithData<GetReviewData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_REVIEW_CHART_DATA,
        method: FetchMethod.POST,
        ...params
    });
}

export function getAccountRatingCounts(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ACCOUNT_RATING_COUNTS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function analyseProductReviews(params: WithData<AnalyseProductReviews> & Callback & WithAbortController) {
    return bolmateFetch({
        url: URLS.POST.ANALYSE_PRODUCT_REVIEWS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSelectableReviewProducts(params: WithData<GetSelectableReviewProducts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SELECTABLE_REVIEW_PRODUCTS,
        method: FetchMethod.POST,
        ...params,
    });
}

/*
 ================================================
 Ratings
 ================================================
 */
interface ExportRatings extends WithAccountID {
    from_date: string | null;
    to_date: string | null;
    only_ratings: Array<Rating> | null;
    ean: string | null;
}

interface GetRatingExportProcesses extends WithAccountID {
    ean: string | null;
}

interface DownloadRatingExport extends WithAccountID {
    fileID: string;
}

interface DeleteRatingsExport extends WithAccountID {
    processID: string;
}

export function exportRatings(params: WithData<ExportRatings> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_RATINGS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getRatingExportProcesses(params: GetRatingExportProcesses & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_RATING_EXPORTS, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

export function downloadRatingExport(params: DownloadRatingExport & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DOWNLOAD_RATING_EXPORT, {account_id: params.account_id, file_id: params.fileID}),
        callback: params.callback
    });
}

export function deleteRatingsExport(params: DeleteRatingsExport & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_RATINGS_EXPORT, {
            account_id: params.account_id,
            process_id: params.processID
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Failed Payments
 ================================================
 */
interface GetFailedPayments extends WithPagination {
    status: PaymentFailureStatus | null;
    search: string | null;
}

interface WithFailedPaymentID {
    failed_payment_id: bigint;
}

interface FailedPaymentAction extends WithFailedPaymentID {
    user_id: string;
}

interface GenerateNewInvoice extends FailedPaymentAction {
    send_immediately?: boolean;
}

export function getFailedPayments(params: WithData<GetFailedPayments> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_FAILED_PAYMENTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function adminGenerateNewInvoice(params: WithData<GenerateNewInvoice> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_GENERATE_NEW_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

export function generateNewInvoice(params: WithData<GenerateNewInvoice> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GENERATE_NEW_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

export function sendFailedPayment(params: FailedPaymentAction & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.SEND_FAILED_PAYMENT,
            {failed_payment_id: params.failed_payment_id.toString(), user_id: params.user_id}),
        callback: params.callback
    });
}

export function deleteFailedPayment(params: FailedPaymentAction & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_FAILED_PAYMENT, {
            failed_payment_id: params.failed_payment_id.toString(),
            user_id: params.user_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Sales dashboard
 ================================================
 */

interface GetSalesDashboardData extends WithAccountID {
    period: SalesDashboardPeriod;
    period_start?: string | null;
    period_end?: string | null;
    combine_account_ids: Array<string> | null;
}

type GetSalesDashboardProducts = GetSalesDashboardData & WithPagination & {
    search: string | null;
    order: SalesDashboardOrder;
    order_direction: OrderDirection;
    filters: SalesFilter;
}

type ExportSalesDashboard = Omit<GetSalesDashboardProducts, 'limit' | 'page'> & {
    export_columns: Array<string>;
}

export function getSalesDashboardProducts(params: WithData<GetSalesDashboardProducts> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SALES_DASHBOARD_PRODUCTS, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function getSalesDashboardStats(params: WithData<GetSalesDashboardData> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SALES_DASHBOARD_STATS, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function getSalesDashboardChartData(params: WithData<GetSalesDashboardData> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SALES_DASHBOARD_CHART_DATA, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function exportSalesDashboard(params: WithData<ExportSalesDashboard> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_SALES_DASHBOARD,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Product costs
 ================================================
 */
interface SetProductCosts extends WithAccountID {
    ean: string;
    costs_id: string | null;
    start_date: string;
    fulfilment: Fulfilment;
    cost_price: number;
    fulfilment_cost: number;
    send_cost: number;
    send_cost_type: SendCostType;
    misc_costs_per_item: IndexedObject<number>;
    misc_costs_per_order: IndexedObject<number>;
}

interface GetProductCosts extends WithAccountID {
    ean: string;
    fulfilment: Fulfilment;
}

interface WithProductCostsID extends WithAccountID {
    costs_id: string;
}

interface ImportProductCosts extends WithAccountID {
    file: File;
    delete_current: boolean;
}

export function updateProductCosts(params: WithData<SetProductCosts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SET_PRODUCT_COSTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getProductCosts(params: GetProductCosts & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PRODUCT_COSTS, {
            account_id: params.account_id,
            ean: params.ean,
            fulfilment: params.fulfilment
        }),
        callback: params.callback
    });
}

export function deleteProductCosts(params: WithProductCostsID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_PRODUCT_COSTS, {
            account_id: params.account_id,
            costs_id: params.costs_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function importProductCosts(params: ImportProductCosts & Callback) {
    const data = new FormData();
    data.set('file', params.file);
    data.set('account_id', params.account_id);
    data.set('delete_current', params.delete_current.toString());
    return bolmateFetch({
        url: URLS.POST.IMPORT_PRODUCT_COSTS,
        method: FetchMethod.POST,
        data,
        stringifyData: false,
        callback: params.callback
    });
}

export function exportProductCosts(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.EXPORT_PRODUCT_COSTS, {account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 User Selection
 ================================================
 */

export interface UserSelectFilter extends WithPagination {
    created_before: string | null;
    created_after: string | null;
    subscription_started_before: string | null;
    subscription_started_after: string | null;
    subscription_status: 'active' | 'inactive' | null;
    include_trials: boolean;
    include_free: boolean;
    include_unpaid: boolean;
}

export function selectUsers(params: WithData<UserSelectFilter> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SELECT_USERS,
        method: FetchMethod.POST,
        ...params
    });
}

export function exportUsers(params: WithData<UserSelectFilter> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_USERS,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 User Estimate
 ================================================
 */

export function getActiveUserEstimate(params: Callback) {
    return bolmateFetch({
        url: URLS.GET.USERS_ESTIMATE, ...params
    });
}

/*
 ================================================
 Search term dashboard
 ================================================
 */
interface GetSearchTermResults extends WithAccountID {
    search_terms: string[],
    period: string,
    number_of_periods: number
    with_comparison?: boolean;
}

interface GetSearchTermAIAnalysis extends WithAccountID {
    search_term: string;
}

export function getSearchVolumes(params: WithData<GetSearchTermResults> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_VOLUMES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchVolumesAIAnalysis(params: WithData<GetSearchTermAIAnalysis> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEARCH_VOLUMES_AI,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Session
 ================================================
 */

export function initSession(callback: VoidFunction) {
    bolmateFetch({
        url: URLS.GET.SESSION,
        callback: (_: ResponseData, response: Response) => {
            const csrf = response.headers.get('X-Csrf-Token');
            if (csrf) setCSRF(csrf);
            callback();
        },
        maintenanceAllowed: true
    });
}

/*
 ================================================
 Account user
 ================================================
 */
type GetAccountUsers = WithAccountID & WithPagination & {
    search: string | null;
}

type UpdateAccountUser = WithUserID & WithAccountID & {
    user_role?: UserRole;
    rights?: Array<UserRight>;
}

type RevokeAccountUser = WithUserID & WithAccountID;

type InviteUser = WithAccountID & {
    email: string;
    user_role: UserRole;
    rights: Array<UserRight>;
}

export function getAccountUsers(params: WithData<GetAccountUsers> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_ACCOUNT_USERS,
        method: FetchMethod.POST,
        ...params
    });
}

export function updateAccountUser(params: WithData<UpdateAccountUser> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_ACCOUNT_USER,
        method: FetchMethod.PUT,
        ...params
    });
}

export function revokeAccountUser(params: RevokeAccountUser & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.REVOKE_ACCOUNT_USER, {user_id: params.user_id, account_id: params.account_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Account invitation
 ================================================
 */
type GetInvitations = WithAccountID & WithPagination & {
    search: string | null;
}

type UpdateInvitation = Partial<InviteUser> & WithAccountID & WithInvitationID;

type WithInvitationID = {invitation_id: string};

type SendUserInvitation = WithAccountID & WithInvitationID;

export function checkInvitation(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.CHECK_INVITATION, {id: params.id}),
        callback: params.callback
    });
}

export function getInvitations(params: WithData<GetInvitations> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_INVITATIONS,
        method: FetchMethod.POST,
        ...params
    });
}

export function inviteAccountUser(params: WithData<InviteUser> & Callback) {
    return bolmateFetch({
        url: URLS.POST.INVITE_ACCOUNT_USER,
        method: FetchMethod.POST,
        ...params
    });
}

export function updateInvitation(params: WithData<UpdateInvitation> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_INVITATION,
        method: FetchMethod.PUT,
        ...params
    });
}

export function sendInvitation(params: WithData<SendUserInvitation> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEND_INVITATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function deleteInvitation(params: SendUserInvitation & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_INVITATION, {
            account_id: params.account_id,
            invitation_id: params.invitation_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Default email templates
 ================================================
 */
interface CreateDefaultEmail {
    template_name: string;
    email_subject: string;
    json_template: UnlayerDesign;
    email_flows: Array<EmailFlow>;
    enabled?: boolean;
    preview_file?: File | null;
}

type UpdateDefaultEmail = WithID & Partial<CreateDefaultEmail> & {
    preview_file_id: string | null;
};

interface GetDefaultEmails {
    email_flows?: Array<EmailFlow>;
}

type GetDefaultEmailsAdmin = WithPagination & GetDefaultEmails & {
    search?: string | null;
    enabled?: boolean | null;
    email_flow?: EmailFlow | null;
};

type TestDefaultEmail = WithAccountID & {
    email_subject: string;
    email_content: string;
    recipient: string;
}

export function createDefaultEmail(params: WithData<CreateDefaultEmail> & Callback) {
    let data: FormData | Record<string, any>;
    let stringify = true;
    if (params.data.preview_file) {
        data = new FormData();
        const paramsData = params.data;
        data.set('template_name', paramsData.template_name);
        data.set('json_template', paramsData.json_template);
        data.set('email_flows', paramsData.email_flows);
        data.set('enabled', paramsData.enabled);
        data.set('preview_file', paramsData.preview_file);
        stringify = false;
    } else data = params.data;
    return bolmateFetch({
        url: URLS.POST.CREATE_DEFAULT_TEMPLATE,
        method: FetchMethod.POST,
        data,
        stringifyData: stringify,
        callback: params.callback
    });
}

export function getDefaultEmailsAdmin(params: WithData<GetDefaultEmailsAdmin> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_GET_DEFAULT_EMAIL_TEMPLATES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getDefaultEmails(params: WithData<GetDefaultEmails> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_DEFAULT_EMAIL_TEMPLATES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getDefaultEmail(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_DEFAULT_EMAIL_TEMPLATE, {id: params.id}),
        callback: params.callback
    });
}

export function updateDefaultEmail(params: WithData<UpdateDefaultEmail> & Callback) {
    let data: FormData | Record<string, any>;
    let stringify = true;
    if (params.data.preview_file) {
        data = new FormData();
        const paramsData = params.data;
        data.set('id', paramsData.id);
        data.set('template_name', paramsData.template_name);
        data.set('json_template', JSON.stringify(paramsData.json_template));
        data.set('email_flows', JSON.stringify(paramsData.email_flows));
        data.set('enabled', paramsData.enabled);
        data.set('preview_file', paramsData.preview_file);
        stringify = false;
    } else data = params.data;
    return bolmateFetch({
        url: URLS.PUT.UPDATE_DEFAULT_EMAIL_TEMPLATE,
        method: FetchMethod.PUT,
        data,
        stringifyData: stringify,
        callback: params.callback
    });
}

export function deleteDefaultEmail(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_DEFAULT_EMAIL_TEMPLATE, {id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function testDefaultEmail(params: WithData<TestDefaultEmail> & Callback) {
    return bolmateFetch({
        url: URLS.POST.TEST_DEFAULT_EMAIL,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Notifications
 ================================================
 */
interface CreateNotification extends WithAccountID {
    notification_type: NotificationType;
    interval: NotificationInterval;
    email_addresses: Array<string>;
    flex: Record<string, any> | null;
}

interface GetNotifications extends WithAccountID {
    notification_type: NotificationType;
}

interface UpdateNotification extends Partial<Omit<CreateNotification, 'notification_type'>> {
    notification_id: string;
}

interface DeleteNotification extends WithAccountID {
    notification_id: string;
}

interface SendTestNotification extends WithAccountID {
    notification_id: string;
    email_address: string;
}

interface NotificationFeedback {
    feedback: string;
}

export function createNotification(params: WithData<CreateNotification> & Callback) {
    return bolmateFetch({
        url: URLS.POST.CREATE_NOTIFICATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function getNotifications(params: GetNotifications & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_NOTIFICATIONS,
            {account_id: params.account_id, notification_type: params.notification_type}),
        callback: params.callback
    });
}

export function updateNotification(params: WithData<UpdateNotification> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_NOTIFICATION,
        method: FetchMethod.PUT,
        ...params
    });
}

export function deleteNotification(params: DeleteNotification & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_NOTIFICATION,
            {account_id: params.account_id, notification_id: params.notification_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function sendTestNotification(params: WithData<SendTestNotification> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEND_TEST_NOTIFICATION,
        method: FetchMethod.POST,
        ...params,
    });
}

export function submitNotificationFeedback(params: WithData<NotificationFeedback> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SUBMIT_NOTIFICATION_FEEDBACK,
        method: FetchMethod.POST,
        ...params,
    });
}

/*
 ================================================
 Util
 ================================================
 */

function bolmateFetch(config: RequestOptions) {
    if (requireMaintenanceLogin() && !config.maintenanceAllowed) return null;
    const csrfToken = getCSRF();
    if (csrfToken)
        config.headers = {...config.headers, 'X-CSRF-Token': csrfToken};
    config.credentials = 'include';
    return controlFetch(config);
}